import { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { downloadXls } from '../../../utils/exportXls';
import { EXPORT_LABELS as LABELS, IMPORT_LABELS } from '../../../utils/labels';

interface BookingExportRow {
  start_date: string;
  start_time: string | null;
  end_date: string;
  end_time: string | null;
  notes: string | null;
  machine: { name: string } | null;
  site: { name: string } | null;
  subcontractor: { name: string } | null;
  contact: { first_name: string; last_name: string } | null;
  pickup_confirmed_at: string | null;
  pickup_contact: { first_name: string; last_name: string } | null;
  handin_confirmed_at: string | null;
  handin_contact: { first_name: string; last_name: string } | null;
}

const HEADERS = [
  LABELS.colClient,
  LABELS.colContact,
  LABELS.colMachine,
  LABELS.colSite,
  LABELS.colStartDate,
  LABELS.colStartTime,
  LABELS.colEndDate,
  LABELS.colEndTime,
  LABELS.colNotes,
  LABELS.colPickupConfirmed,
  LABELS.colPickupContact,
  LABELS.colHandinConfirmed,
  LABELS.colHandinContact,
];

function formatDate(d: string | null): string {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '';
  const [datePart, timePart] = iso.split('T');
  const dateStr = formatDate(datePart);
  if (!timePart) return dateStr;
  return `${dateStr} ${timePart.slice(0, 5)}`;
}

function formatConfirmedContact(
  confirmedAt: string | null,
  contact: { first_name: string; last_name: string } | null,
): string {
  if (!confirmedAt || !contact) return '';
  return `${contact.first_name} ${contact.last_name} (${formatDateTime(confirmedAt)})`;
}

function toRows(data: BookingExportRow[]): string[][] {
  return data.map((b) => [
    b.subcontractor?.name ?? '',
    b.contact ? `${b.contact.first_name} ${b.contact.last_name}` : '',
    b.machine?.name ?? '',
    b.site?.name ?? '',
    formatDate(b.start_date),
    b.start_time ?? '',
    formatDate(b.end_date),
    b.end_time ?? '',
    b.notes ?? '',
    b.pickup_confirmed_at ? IMPORT_LABELS.pickupYes : IMPORT_LABELS.pickupNo,
    formatConfirmedContact(b.pickup_confirmed_at, b.pickup_contact),
    b.handin_confirmed_at ? IMPORT_LABELS.pickupYes : IMPORT_LABELS.pickupNo,
    formatConfirmedContact(b.handin_confirmed_at, b.handin_contact),
  ]);
}

export function useExportBookings(projectId: string | null) {
  const [exporting, setExporting] = useState(false);

  const exportBookings = async (): Promise<void> => {
    if (!projectId) return;
    setExporting(true);
    try {
      const { data, error } = await supabase
        .from('bookings')
        .select(
          'start_date, start_time, end_date, end_time, notes, machine:machines!machine_id(name), site:sites!site_id(name), subcontractor:subcontractors!subcontractor_id(name), contact:subcontractor_users!subcontractor_user_id(first_name, last_name), pickup_contact:subcontractor_users!pickup_subcontractor_user_id(first_name, last_name), pickup_confirmed_at, handin_contact:subcontractor_users!handin_subcontractor_user_id(first_name, last_name), handin_confirmed_at' as const
        )
        .eq('project_id', projectId)
        .order('subcontractor_id')
        .order('start_date');
      if (error) throw error;
      await downloadXls(LABELS.bookingsFilename, HEADERS, toRows(data as BookingExportRow[]));
    } finally {
      setExporting(false);
    }
  };

  return { exportBookings, exporting };
}
